// Priority focus card component shared by Current Action, Build an order,
// Payment review and Subscriptions review. Mirrors the approved
// section.focus layout: eyebrow, heading, WHAT/WHY NOW/NEXT ACTION/OWNERSHIP.
//
// TODO(runtime): hydrate payload from services/priority-engine
// computeTopAction(contactId, {now}) once the HTTP endpoint lands.
//
// W26 (2026-10-20): `owner` and `showRepriority` are optional so the
// Current Action screen can render the real HubSpot owner (or an honest
// "Unassigned") on a real contact.
//
// W32 (2026-10-20): the fallback when `owner` is omitted is now session
// aware. Without a session (design harness, unit tests) or on the dev
// mock session the card keeps the design-preview JAMIE owner, so every
// preview screen is visually unchanged. On a real HubSpot session the
// fallback is an explicit "Unassigned", so JAMIE can never render for a
// real contact even if a caller forgets to pass `owner`. The check reads
// only the session context (no Router dependency).
//
// W40 (2026-10-21): the literal "Continuously reprioritized" pill is
// part of the approved customer task header (CLAUDE-CODE-HANDOFF.md
// decision 2: keep it at the far-right edge, informational, no overlap
// on narrow layouts). W26/W31/W32 had hidden it on real sessions as a
// fixture; it is restored here once, in the shared header, with no
// timestamp and no claim about an engine. `RepriorityPill` is exported
// so the screens that draw their own hero (14-17, 20, 13, companions)
// render the same literal.
import type { ReactNode } from 'react';
import { Icon } from './IconSprite.js';
import { JAMIE } from '../../data/mockData.js';
import { isDevMockSession } from '../../auth/mockContext.js';
import { useOptionalSession } from '../../auth/session-context.js';

export interface PriorityCell {
  label: string;
  heading?: string;
  body?: string;
  detail?: string;
  iconId: string;
}

export interface PriorityOwner {
  initials: string;
  fullName: string;
}

export interface PriorityFocusProps {
  ariaLabel: string;
  eyebrow: string;
  heading: string;
  what: PriorityCell;
  whyNow: PriorityCell;
  nextAction: PriorityCell;
  ownership?: PriorityCell;
  /** Real owner for the OWNERSHIP cell. Defaults to the preview rep, or "Unassigned" on a real session. */
  owner?: PriorityOwner;
}

const PREVIEW_OWNER: PriorityOwner = { initials: JAMIE.initials, fullName: JAMIE.fullName };
const UNASSIGNED: PriorityOwner = { initials: 'UN', fullName: 'Unassigned' };

/** Approved literal for the task-header pill (handoff decision 2). */
export const REPRIORITY_PILL_LABEL = 'Continuously reprioritized';

export interface RepriorityPillProps {
  /** Class hook: `repriority` inside section.focus (01-10, 21, 22), `shared-repriority` in the W2A-lower heroes. */
  className?: string;
}

/** The informational "Continuously reprioritized" pill, rendered once per customer task header. */
export function RepriorityPill({ className = 'repriority' }: RepriorityPillProps): ReactNode {
  return (
    <span className={className} data-component="repriority-pill">
      <Icon id="refresh" />
      {REPRIORITY_PILL_LABEL}
    </span>
  );
}

/** True when a real (non-dev-mock) HubSpot session is active. Shared with NextCustomer. */
export function useIsRealSession(): boolean {
  const ctx = useOptionalSession();
  if (!ctx) return false;
  if (ctx.status !== 'ready' && ctx.status !== 'refreshing') return false;
  if (!ctx.session) return false;
  return !isDevMockSession(ctx.session);
}

export function PriorityFocus(props: PriorityFocusProps): ReactNode {
  const {
    ariaLabel,
    eyebrow,
    heading,
    what,
    whyNow,
    nextAction,
    ownership,
    owner,
  } = props;
  const realSession = useIsRealSession();
  const ownerView: PriorityOwner = owner ?? (realSession ? UNASSIGNED : PREVIEW_OWNER);
  return (
    <section className="focus" data-component="priority-focus" aria-label={ariaLabel}>
      <div className="kicker">
        <div className="priority-heading">
          <span className="priority-icon">
            <Icon id="lightning" />
          </span>
          <div>
            <span className="eyebrow">{eyebrow}</span>
            <h2>{heading}</h2>
          </div>
        </div>
        <RepriorityPill />
      </div>
      <div className="guidance">
        <div>
          <span className="guide-icon">
            <Icon id={what.iconId} />
          </span>
          <span className="label">{what.label}</span>
          {what.heading ? <h1>{what.heading}</h1> : null}
          {what.body ? <p>{what.body}</p> : null}
        </div>
        <div>
          <span className="guide-icon">
            <Icon id={whyNow.iconId} />
          </span>
          <span className="label">{whyNow.label}</span>
          {whyNow.body ? <p>{whyNow.body}</p> : null}
        </div>
        <div>
          <span className="guide-icon">
            <Icon id={nextAction.iconId} />
          </span>
          <span className="label">{nextAction.label}</span>
          {nextAction.body ? <p>{nextAction.body}</p> : null}
          {nextAction.detail ? <small>{nextAction.detail}</small> : null}
        </div>
        <div>
          <span className="guide-icon">
            <Icon id={ownership?.iconId ?? 'people'} />
          </span>
          <span className="label">{ownership?.label ?? 'OWNERSHIP'}</span>
          <div className="owner-inline">
            <span className="mini-avatar">{ownerView.initials}</span>
            {ownerView.fullName}
          </div>
          {ownership?.detail ? (
            <small>{ownership.detail}</small>
          ) : (
            <small>
              {realSession && !owner
                ? 'No HubSpot owner on this contact yet.'
                : 'Primary account owner. Coverage keeps this relationship intact.'}
            </small>
          )}
        </div>
      </div>
    </section>
  );
}
